import React, { useState } from "react";
import { Button, FilterBlock, type FilterFilterParam, useToast } from "@racoon/ui";
import { IcExcel, IcPdf, IcPrint } from "@racoon/icons";
import { Namespace, useT } from "@racoon/i18n";
import { DateValue } from "~/shared/lib/analytics-utils";

interface Props {
  startDate: DateValue;
  endDate: DateValue;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  /** Reports its own failure. */
  onDownloadExcel?: () => Promise<void> | void;
  onDownloadPdf?: () => Promise<void> | void;
  onPrint?: () => void;
  /** Shows the Excel, PDF and print buttons next to the filter button. */
  showActionButtons?: boolean;
  /** The active report's filters, shown in the filter sheet next to the dates. */
  filterParams?: FilterFilterParam[];
  onFilterParamsChange?: (key: string, value: string | null) => void;
  onFilterSheetOpenChange?: (open: boolean) => void;
}

export const AnalyticsFilterBlock: React.FC<Props> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onDownloadExcel,
  onDownloadPdf,
  onPrint,
  showActionButtons = false,
  filterParams,
  onFilterParamsChange,
  onFilterSheetOpenChange,
}) => {
  const t = useT();
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const handleDownloadExcel = async () => {
    setIsExporting(true);
    try {
      await onDownloadExcel?.();
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadPdf = async () => {
    setIsExporting(true);
    try {
      await onDownloadPdf?.();
    } catch (error) {
      console.error("PDF export failed:", error);
      toast({ title: t(Namespace.Analytics, "buttons.pdfFailed"), variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  const actionButtons = (
    <>
      <Button
        id="analytics-download-excel-button"
        variant="outline"
        size="sm"
        className="gap-2 bg-white shrink-0"
        onClick={handleDownloadExcel}
        disabled={isExporting}
        aria-label={t(Namespace.Analytics, "buttons.downloadExcel")}
      >
        <IcExcel className="h-4 w-4 text-status-success" />
        <span className="hidden md:inline">{t(Namespace.Analytics, "buttons.downloadExcel")}</span>
      </Button>
      <Button
        id="analytics-download-pdf-button"
        variant="outline"
        size="sm"
        className="gap-2 bg-white shrink-0"
        onClick={handleDownloadPdf}
        disabled={isExporting}
        aria-label={t(Namespace.Analytics, "buttons.downloadPdf")}
      >
        <IcPdf className="h-4 w-4" />
        <span className="hidden md:inline">{t(Namespace.Analytics, "buttons.downloadPdf")}</span>
      </Button>
      <Button
        id="analytics-print-button"
        variant="outline"
        size="sm"
        className="gap-2 bg-white shrink-0"
        onClick={onPrint}
        aria-label={t(Namespace.Analytics, "buttons.print")}
      >
        <IcPrint className="h-4 w-4" />
        <span className="hidden md:inline">{t(Namespace.Analytics, "buttons.print")}</span>
      </Button>
    </>
  );

  // Convert DateValue to Date for FilterBlock
  const startDateAsDate = new Date(startDate.year, startDate.month - 1, startDate.day);
  const endDateAsDate = new Date(endDate.year, endDate.month - 1, endDate.day);

  // Date change handler for FilterBlock
  const handleDateChange = (key: "startDate" | "endDate", value: Date) => {
    if (key === "startDate") {
      onStartDateChange(value);
    } else {
      onEndDateChange(value);
    }
  };

  // The default column layout spans the controls row, so the buttons sit at its right edge.
  return (
    <FilterBlock
      startDate={startDateAsDate}
      endDate={endDateAsDate}
      onDateChange={handleDateChange}
      additionalButtons={actionButtons}
      additionalButtonsPosition="bottom"
      additionalButtonsVisible={showActionButtons}
      filterParams={filterParams}
      onFilterParamsChange={onFilterParamsChange}
      onFilterSheetOpenChange={onFilterSheetOpenChange}
    />
  );
};
